import jsPDF from 'jspdf';

interface MonthlySavingsExportData {
  salary: number;
  otherIncome: number;
  salaryFrequency: string;
  needs: number;
  wants: number;
  savings: number;
  currency: string;
}

interface TranslationFunction {
  (key: string): string;
}

export const generateMonthlySavingsPDF = (
  data: MonthlySavingsExportData,
  currency: string,
  t: TranslationFunction
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  let yPosition = 30;

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(currency === "USD" ? "en-US" : currency === "GBP" ? "en-GB" : "de-DE", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Helper function to get frequency text
  const getFrequencyText = (frequency: string) => {
    switch (frequency) {
      case "monthly": return t("tools.savings.monthly");
      case "annual": return t("tools.savings.annual");
      default: return frequency;
    }
  };

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - 40) {
      doc.addPage();
      yPosition = 30;
      return true;
    }
    return false;
  };

  // Helper function to add text with line breaks
  const addText = (text: string, x: number, y: number, maxWidth?: number) => {
    if (maxWidth) {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return lines.length * 7; // Return height used
    } else {
      doc.text(text, x, y);
      return 7; // Return single line height
    }
  };

  // PAGE 1: TITLE AND SUMMARY
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("CALCULADORA DE PRESUPUESTO MENSUAL", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text("Reporte de Analisis Financiero Personal", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 25;

  // Summary section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("RESUMEN EJECUTIVO", margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  const totalIncome = data.salary + data.otherIncome;

  const summaryLines = [
    `Fecha del reporte: ${new Date().toLocaleDateString()}`,
    `Ingresos totales (${getFrequencyText(data.salaryFrequency)}): ${formatCurrency(totalIncome)}`,
    `Salario principal: ${formatCurrency(data.salary)}`,
    `Otros ingresos: ${formatCurrency(data.otherIncome)}`,
    `Moneda: ${currency}`,
    "",
    "DISTRIBUCION RECOMENDADA 50/30/20:",
    `Necesidades (50%): ${formatCurrency(data.needs)}`,
    `Ahorros (30%): ${formatCurrency(data.savings)}`,
    `Deseos (20%): ${formatCurrency(data.wants)}`
  ];

  summaryLines.forEach(line => {
    if (line === "") {
      yPosition += 5;
    } else {
      doc.text(line, margin, yPosition);
      yPosition += 8;
    }
  });

  yPosition += 20;

  // NEEDS SECTION
  checkPageBreak(80);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("NECESIDADES (50%) - " + formatCurrency(data.needs), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  const needsText = `Las necesidades son gastos esenciales que no puedes evitar. Este 50% de tus ingresos debe cubrir todos los gastos basicos para vivir.`;
  yPosition += addText(needsText, margin, yPosition, pageWidth - 2 * margin);
  yPosition += 10;

  const needsBreakdown = [
    "DISTRIBUCION SUGERIDA PARA NECESIDADES:",
    "",
    `Vivienda (alquiler/hipoteca): 30-35% = ${formatCurrency(data.needs * 0.32)} - ${formatCurrency(data.needs * 0.35)}`,
    `Servicios publicos (luz, agua, gas): 7-12% = ${formatCurrency(data.needs * 0.07)} - ${formatCurrency(data.needs * 0.12)}`,
    `Alimentacion basica: 10-15% = ${formatCurrency(data.needs * 0.10)} - ${formatCurrency(data.needs * 0.15)}`,
    `Transporte al trabajo: 8-12% = ${formatCurrency(data.needs * 0.08)} - ${formatCurrency(data.needs * 0.12)}`,
    `Seguro de salud: 5-10% = ${formatCurrency(data.needs * 0.05)} - ${formatCurrency(data.needs * 0.10)}`,
    `Gastos medicos basicos: 3-7% = ${formatCurrency(data.needs * 0.03)} - ${formatCurrency(data.needs * 0.07)}`,
    `Productos de higiene personal: 2-4% = ${formatCurrency(data.needs * 0.02)} - ${formatCurrency(data.needs * 0.04)}`,
    `Ropa basica y calzado: 3-5% = ${formatCurrency(data.needs * 0.03)} - ${formatCurrency(data.needs * 0.05)}`,
    "",
    "CONSEJOS PARA OPTIMIZAR NECESIDADES:",
    "- Revisa contratos de servicios anualmente para encontrar mejores tarifas",
    "- Planifica las comidas semanalmente para evitar gastos innecesarios",
    "- Considera opciones de transporte publico cuando sea viable",
    "- Mantén un seguro de salud basico pero adecuado",
    "- Compra productos de higiene y limpieza al por mayor cuando hay ofertas"
  ];

  needsBreakdown.forEach(line => {
    checkPageBreak(10);
    if (line === "") {
      yPosition += 5;
    } else if (line.startsWith("DISTRIBUCION") || line.startsWith("CONSEJOS")) {
      doc.setFont("helvetica", "bold");
      doc.text(line, margin, yPosition);
      doc.setFont("helvetica", "normal");
      yPosition += 10;
    } else {
      yPosition += addText(line, margin, yPosition, pageWidth - 2 * margin);
      yPosition += 2;
    }
  });

  // SAVINGS SECTION
  checkPageBreak(80);
  yPosition += 15;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("AHORROS E INVERSIONES (30%) - " + formatCurrency(data.savings), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  const savingsText = `Esta categoria incluye todo lo destinado a tu futuro financiero. Es la base para construir riqueza y seguridad financiera a largo plazo.`;
  yPosition += addText(savingsText, margin, yPosition, pageWidth - 2 * margin);
  yPosition += 10;

  const savingsBreakdown = [
    "DISTRIBUCION SUGERIDA PARA AHORROS:",
    "",
    `Fondo de emergencia (40%): ${formatCurrency(data.savings * 0.40)}`,
    "  - Meta: 3-6 meses de gastos esenciales",
    "  - Mantener en cuenta de ahorro de facil acceso",
    "",
    `Jubilacion (35%): ${formatCurrency(data.savings * 0.35)}`,
    "  - Planes de pension privados",
    "  - Fondos de inversion a largo plazo",
    "",
    `Objetivos a corto plazo (15%): ${formatCurrency(data.savings * 0.15)}`,
    "  - Vacaciones, electrodomesticos, cursos",
    "  - Metas de 1-3 años",
    "",
    `Inversiones (10%): ${formatCurrency(data.savings * 0.10)}`,
    "  - Acciones, fondos indexados",
    "  - Crecimiento del capital",
    "",
    "ESTRATEGIAS DE AHORRO RECOMENDADAS:",
    "- Automatiza transferencias: configura ahorros automaticos cada mes",
    "- Regla 24 horas: espera un dia antes de compras no planificadas",
    "- Ahorro de cambio: redondea compras y ahorra la diferencia",
    "- Incrementa ahorro con aumentos salariales",
    "- Revisa y ajusta objetivos cada 6 meses",
    "",
    "OPCIONES DE INVERSION POR PERFIL:",
    "Conservador: Cuentas de ahorro, depositos a plazo, bonos del estado",
    "Moderado: Fondos mixtos, ETFs diversificados, algunos bonos corporativos",
    "Agresivo: Acciones individuales, fondos de crecimiento, REITs"
  ];

  savingsBreakdown.forEach(line => {
    checkPageBreak(10);
    if (line === "") {
      yPosition += 5;
    } else if (line.startsWith("DISTRIBUCION") || line.startsWith("ESTRATEGIAS") || line.startsWith("OPCIONES")) {
      doc.setFont("helvetica", "bold");
      doc.text(line, margin, yPosition);
      doc.setFont("helvetica", "normal");
      yPosition += 10;
    } else if (line.startsWith("  - ")) {
      doc.text(line, margin + 10, yPosition);
      yPosition += 8;
    } else {
      yPosition += addText(line, margin, yPosition, pageWidth - 2 * margin);
      yPosition += 2;
    }
  });

  // WANTS SECTION
  checkPageBreak(80);
  yPosition += 15;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("DESEOS Y ENTRETENIMIENTO (20%) - " + formatCurrency(data.wants), margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  const wantsText = `Los deseos son gastos que mejoran tu calidad de vida pero no son esenciales para sobrevivir. Este presupuesto te permite disfrutar sin comprometer tu futuro financiero.`;
  yPosition += addText(wantsText, margin, yPosition, pageWidth - 2 * margin);
  yPosition += 10;

  const wantsBreakdown = [
    "DISTRIBUCION SUGERIDA PARA DESEOS:",
    "",
    `Entretenimiento (40%): ${formatCurrency(data.wants * 0.40)}`,
    "  - Cine, streaming, conciertos, deportes",
    "",
    `Restaurantes y comida externa (25%): ${formatCurrency(data.wants * 0.25)}`,
    "  - Cenas fuera, delivery, cafeterias",
    "",
    `Hobbies y aficiones (20%): ${formatCurrency(data.wants * 0.20)}`,
    "  - Libros, musica, deportes, manualidades",
    "",
    `Compras no esenciales (15%): ${formatCurrency(data.wants * 0.15)}`,
    "  - Ropa de moda, gadgets, decoracion",
    "",
    "CONSEJOS PARA GASTOS INTELIGENTES:",
    "- Establece un presupuesto mensual especifico para cada categoria",
    "- Busca ofertas y descuentos antes de comprar",
    "- Considera alternativas gratuitas (parques vs cine)",
    "- Practica la regla 30 dias para compras grandes",
    "- Prioriza experiencias sobre objetos materiales",
    "- Usa aplicaciones para rastrear gastos de entretenimiento",
    "",
    "ALTERNATIVAS ECONOMICAS:",
    "- Cocinar en casa vs restaurantes",
    "- Bibliotecas publicas vs comprar libros",
    "- Actividades al aire libre vs entretenimiento pago",
    "- Intercambio de servicios con amigos",
    "- Eventos gratuitos en tu ciudad"
  ];

  wantsBreakdown.forEach(line => {
    checkPageBreak(10);
    if (line === "") {
      yPosition += 5;
    } else if (line.startsWith("DISTRIBUCION") || line.startsWith("CONSEJOS") || line.startsWith("ALTERNATIVAS")) {
      doc.setFont("helvetica", "bold");
      doc.text(line, margin, yPosition);
      doc.setFont("helvetica", "normal");
      yPosition += 10;
    } else if (line.startsWith("  - ")) {
      doc.text(line, margin + 10, yPosition);
      yPosition += 8;
    } else {
      yPosition += addText(line, margin, yPosition, pageWidth - 2 * margin);
      yPosition += 2;
    }
  });

  // ADDITIONAL RECOMMENDATIONS
  checkPageBreak(80);
  yPosition += 15;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("RECOMENDACIONES ADICIONALES", margin, yPosition);
  yPosition += 15;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");

  const additionalRecommendations = [
    "SITUACIONES DE EMERGENCIA:",
    "- Perdida de empleo: usar fondo de emergencia, reducir gastos no esenciales",
    "- Gastos medicos inesperados: priorizar salud, ajustar temporalmente otros gastos",
    "- Reparaciones mayores: evaluar necesidad, buscar multiples cotizaciones",
    "",
    "CUANDO AJUSTAR TU PRESUPUESTO:",
    "- Cambios en ingresos (aumento o disminucion)",
    "- Cambios en gastos fijos (mudanza, nuevo prestamo)",
    "- Objetivos financieros diferentes",
    "- Cambios en situacion familiar",
    "- Revision trimestral de progreso",
    "",
    "METRICAS DE EXITO FINANCIERO:",
    `- Tasa de ahorro actual: ${((data.savings / (data.salary + data.otherIncome)) * 100).toFixed(1)}% (Meta: 30%)`,
    "- Fondo de emergencia: Meta de 3-6 meses de gastos esenciales",
    "- Deuda vs ingresos: Meta menor al 30% de ingresos",
    "- Crecimiento patrimonial: Meta 5-10% anual despues de inflacion",
    "",
    "PROXIMOS PASOS RECOMENDADOS:",
    "1. Implementa el presupuesto 50/30/20 gradualmente",
    "2. Automatiza tus ahorros e inversiones",
    "3. Revisa y ajusta mensualmente",
    "4. Considera consultar un asesor financiero para objetivos complejos",
    "5. Educate continuamente sobre finanzas personales"
  ];

  additionalRecommendations.forEach(line => {
    checkPageBreak(10);
    if (line === "") {
      yPosition += 5;
    } else if (line.includes(":") && !line.startsWith("-") && !line.startsWith("1.") && !line.startsWith("2.") && !line.startsWith("3.") && !line.startsWith("4.") && !line.startsWith("5.")) {
      doc.setFont("helvetica", "bold");
      doc.text(line, margin, yPosition);
      doc.setFont("helvetica", "normal");
      yPosition += 10;
    } else {
      yPosition += addText(line, margin, yPosition, pageWidth - 2 * margin);
      yPosition += 2;
    }
  });

  // Footer on all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(
      `Generado el ${new Date().toLocaleDateString()} por Finanzly - Pagina ${i} de ${totalPages}`,
      pageWidth / 2,
      pageHeight - 15,
      { align: "center" }
    );
  }

  return doc;
};